import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TableLazyLoadEvent } from 'primeng/table';
import { HasPermissionDirective } from '../../../../../../directives/has-permission.directive';
import {
  AuscultationDataTypes,
  AuscultationInformationDefinition,
} from '../../../../../../entity-definitions/auscultation-information.definition';
import { ToastService } from '../../../../../../services/toast.service';
import { AssetStore } from '../../../../../../stores/asset.store';
import { AuscultationInformationStore } from '../../../../../../stores/auscultation-info.store';
import { ChoiceListStore } from '../../../../../../stores/choice-list.store';
import { LocationStore } from '../../../../../../stores/location.store';
import { UsedLocationOptionsService } from '../../../../../../services/used-location-options.service';
import { buildLocationFilterColumn } from '../../../../../../utils/location-filter';
import { DetailedAuscultationInformation } from '../../../../../../types/detailed-auscultation-information.interface';
import { ColumnConfig } from '../../../../../../types/entity-definition.interface';
import { LazyLoadParams } from '../../../../../../types/lazy-load.interface';
import { PaginatedListHelper } from '../../../../../../utils/paginated-list.helper';
import { RouteUtils } from '../../../../../../utils/route.utils';
import { GenericParentGridComponent } from '../../../../../generic/generic-parent-grid.component';

// Le libellé traduit d'un type de donnée n'existe que côté UI : le défaut
// « type A-Z puis nom » se trie donc client, en stable sur l'ordre nom A-Z
// déjà servi — valable tant qu'un ouvrage tient sur une page (≤6 infos par
// ouvrage en pratique, pages de 50).
export function sortByTypeLabel(
  rows: readonly DetailedAuscultationInformation[],
  labelByType: ReadonlyMap<number, string>,
): DetailedAuscultationInformation[] {
  return [...rows].sort((a, b) =>
    (labelByType.get(a.type) ?? '').localeCompare(labelByType.get(b.type) ?? '', undefined, { sensitivity: 'base' }),
  );
}

@Component({
  selector: 'app-measure-auscultation',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GenericParentGridComponent, HasPermissionDirective, TranslocoModule],
  templateUrl: './auscultation-info-list.component.html',
})
export class AuscultationInformationsListComponent implements OnInit {
  private readonly translateService = inject(TranslocoService);
  private readonly translations = toSignal(this.translateService.selectTranslation());
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly assetStore = inject(AssetStore);
  private readonly auscultationInfoStore = inject(AuscultationInformationStore);
  private readonly locationStore = inject(LocationStore);
  private readonly usedLocations = inject(UsedLocationOptionsService);
  private readonly choiceListStore = inject(ChoiceListStore);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly helper = new PaginatedListHelper<DetailedAuscultationInformation>(this.translateService, {
    globalFilterFields: ['name', 'localisationName'],
    errorKey: 'common.errors.auscultationInfoLoadFailed',
    stateKey: 'auscultation-info-grid',
    loadAssetById: async id => {
      await this.assetStore.loadById(id);
    },
    isAssetLoaded: id => !!this.assetStore.byId()(id),
  });

  readonly auscultationDataTypes = AuscultationDataTypes;

  readonly entityDefinitionWithPermissions = computed(() => {
    this.translations();
    const columns: ColumnConfig<DetailedAuscultationInformation>[] = [
      {
        field: 'name',
        headerKey: 'common.labels.name',
        sortable: true,
        filterable: true,
        class: 'min-w-[200px]',
        contentClass: 'line-clamp-1',
        showTooltip: true,
      },
      {
        field: 'type',
        headerKey: 'common.labels.type',
        template: 'optionValue' as const,
        options: this.auscultationDataTypes,
        sortable: true,
        filterable: 'multiselect' as const,
      },
      buildLocationFilterColumn<DetailedAuscultationInformation>(this.usedLocations.options('auscultation')(), 'server'),
    ];
    const assetId = this.helper.state.currentAssetId() ?? undefined;
    return {
      ...AuscultationInformationDefinition,
      defaultSortField: null,
      list: { columns },
      actionButtons: {
        ...AuscultationInformationDefinition.actionButtons,
        parent: {
          ...AuscultationInformationDefinition.actionButtons?.parent,
          edit: false,
          addPermission: { method: 'postInfoList' as const, assetId },
          editPermission: { method: 'putInfoDetail' as const, assetId },
          deletePermission: { method: 'deleteInfoDetail' as const, assetId },
        },
      },
      rowLinkHref: (info: DetailedAuscultationInformation) =>
        info.assetId ? RouteUtils.getMeasurePath(info.assetId, 'auscultation-information', info.id) : null,
    };
  });

  readonly tablePermissionConfig = computed(() => ({
    method: 'getInfoDetail' as const,
    assetId: this.helper.state.currentAssetId() ?? undefined,
  }));

  readonly gridStateKey = computed(() => this.helper.buildStateKey('auscultation-info-grid'));

  constructor() {
    effect(() => {
      const assetId = this.helper.state.currentAssetId();
      this.usedLocations.load(assetId, 'auscultation');
      if (assetId) {
        this.locationStore.getLocationsByAsset(assetId).catch((error: unknown) => {
          console.error(this.translateService.translate('common.errors.auscultationInfoLoadFailed'), error);
        });
        this.choiceListStore.loadAll().catch((error: unknown) => {
          console.error(this.translateService.translate('common.errors.auscultationInfoLoadFailed'), error);
        });
      }
    });
  }

  ngOnInit(): void {
    this.helper.setupLangChangeSubscription(this.destroyRef, () =>
      this.loadData(this.helper.lastParams ?? this.helper.createInitialParams()),
    );
    this.helper.setupRouteSubscription(this.route, this.destroyRef, async shouldLoad => {
      if (shouldLoad) {
        await this.loadData(this.helper.createInitialParams());
      }
    });
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    await this.helper.handleLazyLoad(event, params => this.loadData(params));
  }

  private async loadData(params: LazyLoadParams): Promise<void> {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId) return;

    await this.helper.executeLoad(params, async () => {
      const response = await this.auscultationInfoStore.loadDetailedPaginated({ assetId, ...params });
      // Un tri de colonne choisi par l'utilisateur prime sur le défaut.
      if (params.sorts?.length) return response;
      const labelByType = new Map<number, string>(
        this.auscultationDataTypes.map(({ value, labelKey }) => [value, this.translateService.translate(labelKey)]),
      );
      return { ...response, data: sortByTypeLabel(response.data, labelByType) };
    });
  }

  createAuscultationInfo(): void {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId) return;
    void this.router.navigate(['/assets', assetId, 'configuration', 'measures', 'auscultation-information', 'new']);
  }

  async deleteAuscultationInfo(info: DetailedAuscultationInformation): Promise<void> {
    if (!info?.id) {
      console.error(this.translateService.translate('common.errors.invalidAuscultationInfoDeletion'));
      return;
    }

    try {
      await this.auscultationInfoStore.delete(info.id);
      await this.loadData(this.helper.lastParams ?? this.helper.createInitialParams());
    } catch (error) {
      this.toastService.warning('entities.auscultationInformation.messages.deleteError');
      console.error(this.translateService.translate('common.errors.auscultationInfoDeleteFailed'), error);
    }
  }
}
